/**
 * PWA (kap. 28): registrace service workeru, nabídka instalace, verze a úklid offline dat.
 * `classifyRequest` zrcadlí logiku v public/sw.js, aby ji šlo testovat.
 */

export const API_CACHEABLE = [
  '/api/v1/auth/me',
  '/api/v1/companies',
  '/api/v1/documents',
  '/api/v1/contacts',
  '/api/v1/stock/',
  '/api/v1/dashboard',
  '/api/v1/payments',
  '/api/v1/overview',
];

export type RequestKind = 'network' | 'assets' | 'api' | 'navigate' | 'shell';

export function classifyRequest(method: string, pathname: string, mode = 'cors'): RequestKind {
  if (method !== 'GET') return 'network';
  if (pathname.startsWith('/assets/')) return 'assets';
  if (pathname.startsWith('/api/')) {
    return API_CACHEABLE.some((p) => pathname === p || pathname.startsWith(p)) ? 'api' : 'network';
  }
  if (mode === 'navigate') return 'navigate';
  return 'shell';
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let installPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

/** Zachytí nabídku instalace (Chrome/Edge) – tlačítko „Nainstalovat" v Účtu. */
export function watchInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    notify();
  });
}

export function canInstall(): boolean {
  return installPrompt !== null;
}

export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!installPrompt) return 'unavailable';
  await installPrompt.prompt();
  const { outcome } = await installPrompt.userChoice;
  installPrompt = null;
  notify();
  return outcome;
}

export function onPwaChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let waiting: ServiceWorker | null = null;

/** Zaregistruje SW (jen v produkčním buildu) a hlásí čekající novou verzi. */
export function registerServiceWorker(onUpdate?: () => void): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker
    .register('/sw.js')
    .then((reg) => {
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            waiting = sw;
            onUpdate?.();
            notify();
          }
        });
      });
    })
    .catch(() => undefined);
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

export function hasUpdate(): boolean {
  return waiting !== null;
}

export function applyUpdate(): void {
  waiting?.postMessage('SKIP_WAITING');
}

export function swActive(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
}

export async function swVersion(): Promise<string | null> {
  const ctrl = typeof navigator !== 'undefined' ? navigator.serviceWorker?.controller : null;
  if (!ctrl) return null;
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), 1500);
    channel.port1.onmessage = (e) => {
      clearTimeout(timer);
      resolve((e.data as { version?: string })?.version ?? null);
    };
    ctrl.postMessage('GET_VERSION', [channel.port2]);
  });
}

/** Smaže všechny cache a odregistruje SW – „Vymazat offline data". */
export async function clearOfflineData(): Promise<number> {
  let n = 0;
  if (typeof caches !== 'undefined') {
    const keys = await caches.keys();
    for (const k of keys) {
      if (await caches.delete(k)) n += 1;
    }
  }
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) await r.unregister();
  }
  return n;
}

export async function cacheSize(): Promise<number> {
  if (typeof caches === 'undefined') return 0;
  let n = 0;
  for (const k of await caches.keys()) {
    const c = await caches.open(k);
    n += (await c.keys()).length;
  }
  return n;
}
